import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  BarChart3,
  MessageSquare,
  Video,
  GraduationCap,
  AlertCircle,
  ShieldCheck,
  BookOpen,
  HelpCircle,
  ClipboardCheck,
  Settings2,
  Inbox,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { useSchoolFeatures } from "@/hooks/useSchoolFeatures";
import { useTenantConfig } from "@/contexts/TenantConfigContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  BEAUTY_SNS_LAB_BRAND,
  isBeautySnsLabConfig,
  isBeautySnsLabHost,
} from "@/config/beautySnsLab";

const menuItems = [
  { title: "ダッシュボード", url: "/dashboard", icon: LayoutDashboard },
  { title: "受講生管理", url: "/students", icon: Users },
  { title: "コース管理", url: "/courses", icon: GraduationCap },
  { title: "分析", url: "/analytics", icon: BarChart3 },
  { title: "AIに相談", url: "/chat", icon: MessageSquare },
  { title: "動画管理", url: "/videos", icon: Video },
  { title: "FAQ分析", url: "/admin/faq", icon: HelpCircle },
  { title: "エスカレーション", url: "/escalations", icon: AlertCircle },
  { title: "ヘルプ", url: "/docs", icon: BookOpen },
];

const featureMenuItems = [
  { title: "台本リスクチェック", url: "/risk-checker", icon: ShieldCheck, feature: "risk_checker" as const },
];

const adminMenuItems = [
  { title: "クイズ設定", url: "/admin/oem/quiz-settings", icon: ClipboardCheck },
  { title: "LINE設定", url: "/admin/line-settings", icon: Settings2 },
];

const superAdminMenuItems: typeof adminMenuItems = [];

const staffMenuItems = [
  { title: "LINE受信ログ", url: "/admin/line-messages", icon: Inbox },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const { config } = useTenantConfig();
  const { user } = useAuth();

  const isCollapsed = state === "collapsed";
  const features = useSchoolFeatures();
  const isBeautyTenant =
    isBeautySnsLabConfig(config) ||
    (typeof window !== "undefined" && isBeautySnsLabHost(window.location.hostname));
  // risk_checker は Beautyでも表示、他の feature は従来通り isBeautyTenant で非表示
  const visibleFeatureItems = featureMenuItems.filter(
    (item) => features[item.feature] && (item.feature === "risk_checker" || !isBeautyTenant)
  );
  const visibleMenuItems = [
    ...menuItems,
    ...staffMenuItems,
    ...(user?.role === "admin" ? adminMenuItems : []),
    ...(user?.isSuperAdmin ? superAdminMenuItems : []),
  ].filter((item) => {
    if (item.url === "/docs" && isBeautyTenant) return false;
    if (
      item.url === "/escalations" &&
      (isBeautyTenant || config?.feature_flags?.human_escalation_enabled === false)
    ) {
      return false;
    }
    return true;
  });
  const sidebarLabel =
    config?.brand?.name || (isBeautyTenant ? BEAUTY_SNS_LAB_BRAND.name : "学習管理システム");
  const logoUrl =
    config?.brand?.logo_url || (isBeautyTenant ? BEAUTY_SNS_LAB_BRAND.logo_url : null);

  return (
    <Sidebar
      className={isCollapsed ? "w-14" : "w-60"}
      collapsible="icon"
      role="navigation"
      aria-label="メインナビゲーション"
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className={isCollapsed ? "sr-only" : "flex h-16 items-center gap-2 px-2 py-2 mb-2"}>
            {logoUrl ? (
              <img src={logoUrl} alt={sidebarLabel} className="h-12 max-w-[190px] object-contain" />
            ) : (
              sidebarLabel
            )}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu aria-label="メニュー" className="space-y-1">
              {visibleMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-muted text-primary font-medium"
                          : "hover:bg-muted/50"
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="h-4 w-4" aria-hidden="true" />
                          {!isCollapsed && <span>{item.title}</span>}
                          {isActive && <span className="sr-only">（現在のページ）</span>}
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {visibleFeatureItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-muted text-primary font-medium"
                          : "hover:bg-muted/50"
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="h-4 w-4" aria-hidden="true" />
                          {!isCollapsed && <span>{item.title}</span>}
                          {isActive && <span className="sr-only">（現在のページ）</span>}
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter>
        {!isCollapsed && (
          <div className="p-2 space-y-2">
            <Separator />
            <div className="flex justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/terms" className="hover:text-primary underline">
                利用規約
              </Link>
              <Link to="/privacy" className="hover:text-primary underline">
                プライバシー
              </Link>
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
